import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';
import pt from 'prop-types';
import cx from 'classnames';
import isEqual from 'lodash/isEqual';
import isEqualWith from 'lodash/isEqualWith';

import {
  resolveSectionSettings,
  getSectionSettingsFromProps,
  scopeCustomCSS,
  toBlockId,
} from '@swell/apps-sdk';

import { SectionOverlay } from 'components/storefront/editor/easyblocks-page/components/overlay';
import InlineEditable, {
  useInlineEditable,
} from 'components/storefront/editor/easyblocks-page/components/inline-editable';
import {
  removeSwellPagePrefix,
  getParentFocusedField,
} from 'components/storefront/editor/utils/easyblocks';
import {
  MESSAGE_EVENTS,
  postEasyblocksFocus,
  postStorefrontItemHover,
  postStorefrontItemUnhover,
} from 'components/storefront/editor/utils/post-message';

import parse from './parser';
import { compileSectionData } from './utils';

function sectionDataCompareFunc(first, second) {
  // compare _id for storefront resources
  if (first?._getter && second?._getter) {
    return first._id === second._id;
  }

  // return undefined to use standard comparison
}

function Section(props) {
  const {
    Root,
    Blocks: blocks,
    __easyblocks: easyblocks,
    className,
    sectionSchema,
    sectionGroups,
    custom_css: customCSS,
    assetLoader,
    theme,
    overlayRootRef,
  } = props;
  const { id: easyblocksId, path } = easyblocks;

  const [template, setTemplate] = useState('');
  const [, setElements] = useState([]);
  const [blockElements, setBlockElements] = useState([]);
  const [inlineEditableConfigs, setInlineEditableConfigs] = useState([]);
  const [hovered, setHovered] = useState(false);
  const [blockHovered, setBlockHovered] = useState(false);
  const [sidebarHovered, setSidebarHovered] = useState(false);

  const { editing, locale, defaultLocale, setEditing } = useInlineEditable();
  const sectionDataRef = useRef(null);
  const blockDataRef = useRef({});
  /** @type {React.MutableRefObject<HTMLDivElement | null>} */
  const contentRef = useRef(null);

  const focusedField = getParentFocusedField();
  const selected = path === focusedField;
  const blockSelected = focusedField !== path && focusedField.includes(path);

  const id = useMemo(() => easyblocksId.replace('.', ''), [easyblocksId]);
  const normalizedPath = useMemo(() => removeSwellPagePrefix(path), [path]);
  const sectionClassName = useMemo(() => theme.getSectionClassName(), [theme]);

  const blockConfigs = useMemo(() => {
    return (sectionSchema.blocks || []).reduce((acc, block) => {
      acc[block.type] = block;

      return acc;
    }, {});
  }, [sectionSchema]);

  const sectionData = useMemo(() => {
    const settingProps = getSectionSettingsFromProps(props, sectionSchema);

    const sectionData = resolveSectionSettings(theme, {
      settings: {
        section: {
          ...settingProps,
          id,
        },
      },
      schema: {
        ...sectionSchema,
        id,
      },
    });

    // special comparison for section storefront resources
    if (
      !isEqualWith(sectionDataRef.current, sectionData, sectionDataCompareFunc)
    ) {
      sectionDataRef.current = sectionData;
    }

    return sectionDataRef.current;
  }, [id, props, sectionSchema, theme]);

  const blocksData = useMemo(() => {
    const data = (blocks || []).reduce((acc, block) => {
      const { path, compiled } = block.props;
      const { _id: id, _component: component, disabled } = compiled;

      if (!component) {
        return acc;
      }

      const blockId = toBlockId(id);
      const blockType = component.split('__')[2];
      const blockSchema = (sectionSchema.blocks || []).find(
        (blockSchema) => blockSchema.type === blockType,
      );

      if (!blockSchema) {
        return acc;
      }

      acc[blockId] = {
        id: blockId,
        path,
        disabled,
        schema: blockSchema,
      };

      return acc;
    }, {});

    if (!isEqual(blockDataRef.current, data)) {
      blockDataRef.current = data;
    }

    return blockDataRef.current;
  }, [blocks, sectionSchema]);

  const scopedCustomCSS = useMemo(() => {
    const hasCSS = customCSS?.length > 0;

    if (!hasCSS) {
      return null;
    }

    return scopeCustomCSS(customCSS, id);
  }, [customCSS, id]);

  useEffect(() => {
    let canceled = false;

    async function renderThemeTemplate() {
      // Prevent rerender during inline editing
      if (editing) {
        return;
      }

      console.log('render section', sectionSchema.id, sectionData);

      const compiledSectionData = await compileSectionData(theme, sectionData);

      if (canceled) return;

      const template = await theme.renderThemeTemplate(
        `theme/sections/${sectionSchema.id}.liquid`,
        compiledSectionData,
      );

      if (canceled) return;

      setTemplate(template);
    }

    renderThemeTemplate();

    return () => {
      canceled = true;
    };
  }, [editing, sectionData, sectionSchema, theme, theme.globals?.settings]);

  useEffect(() => {
    // Prevent rerender during inline editing
    if (!template || editing) {
      return;
    }

    setElements((prevElements) => {
      const { elements, blockElements, inlineEditableConfigs } = parse(
        template,
        theme,
        blocksData,
        assetLoader,
        prevElements,
      );

      if (contentRef.current) {
        contentRef.current.replaceChildren(...elements);

        setBlockElements(blockElements);
        setInlineEditableConfigs(inlineEditableConfigs);

        if (scopedCustomCSS) {
          const style = document.createElement('style');

          style.textContent = scopedCustomCSS;
          contentRef.current.appendChild(style);
        }

        // Simulate Shopify section load event so theme scripts (e.g., animations, section JS) reinitialize after re-render.
        if (theme.shopifyCompatibility) {
          document.dispatchEvent(
            new CustomEvent('shopify:section:load', {
              bubbles: true,
              target: contentRef.current,
            }),
          );
        }

        assetLoader.onSectionMount(normalizedPath);
      }

      return elements;
    });
  }, [
    template,
    editing,
    theme,
    blocksData,
    scopedCustomCSS,
    assetLoader,
    normalizedPath,
  ]);

  useEffect(() => {
    const eventListener = (event) => {
      const { type, payload } = event.data;

      switch (type) {
        case MESSAGE_EVENTS.STOREFRONT_EDITOR.ITEM.HOVER:
          if (payload.path.includes(normalizedPath)) {
            setHovered(true);
            setSidebarHovered(true);

            if (payload.path !== normalizedPath) {
              setBlockHovered(true);
            }
          }

          break;

        case MESSAGE_EVENTS.STOREFRONT_EDITOR.ITEM.UNHOVER:
          if (payload.path.includes(normalizedPath)) {
            setHovered(false);
            setSidebarHovered(false);

            if (payload.path !== normalizedPath) {
              setBlockHovered(false);
            }
          }

          break;

        default:
          break;
      }
    };

    window.addEventListener('message', eventListener);

    return () => {
      window.removeEventListener('message', eventListener);
    };
  }, [normalizedPath]);

  const onMouseEnter = useCallback(() => {
    postStorefrontItemHover(normalizedPath, window.parent);
    setHovered(true);
  }, [normalizedPath]);

  const onMouseLeave = useCallback(() => {
    postStorefrontItemUnhover(normalizedPath, window.parent);
    setHovered(false);
  }, [normalizedPath]);

  const onClick = useCallback(
    (event) => {
      event.stopPropagation();

      // do not select already selected section
      if (selected) {
        return;
      }
      postEasyblocksFocus(normalizedPath, window.parent);
    },
    [normalizedPath, selected],
  );

  const RootComponent = Root.type;

  return (
    <RootComponent
      {...Root.props}
      id={id}
      ref={contentRef}
      className={cx(
        'swell-page-section',
        sectionClassName,
        sectionSchema.class,
        className,
      )}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      {assetLoader.loaded && (
        <>
          {createPortal(
            <SectionOverlay
              id={id}
              path={normalizedPath}
              selected={selected || blockSelected}
              blockSelected={blockSelected}
              hovered={hovered}
              blockHovered={blockHovered}
              sidebarHovered={sidebarHovered}
              sectionGroups={sectionGroups}
              sectionSchema={sectionSchema}
              contentRef={contentRef}
            />,
            overlayRootRef.current,
          )}
          {blockElements.map((blockElement) => {
            const { id: blockId, element } = blockElement;
            const Root = blocks.find(
              (block) => toBlockId(block.props.compiled._id) === blockId,
            );
            const blockData = blocksData[blockId];

            if (!Root || !blockData) {
              return null;
            }

            const RootComponent = Root.type;

            return (
              <RootComponent
                {...Root.props}
                key={blockId}
                blockData={blockData}
                blockConfigs={blockConfigs}
                element={element}
                sectionSelected={selected || blockSelected}
                sectionHovered={hovered}
                overlayRootRef={overlayRootRef}
              />
            );
          })}
          {inlineEditableConfigs.map((config, index) => {
            const { container, content, type, path, fieldId } = config;

            return createPortal(
              <InlineEditable
                type={type}
                path={path}
                fieldId={fieldId}
                content={content}
                locale={locale}
                defaultLocale={defaultLocale}
                setEditing={setEditing}
              />,
              container,
              `${path}.${index}`,
            );
          })}
        </>
      )}
    </RootComponent>
  );
}

Section.propTypes = {
  Root: pt.element,
  Blocks: pt.array,
  __easyblocks: pt.object,
  id: pt.string,
  className: pt.string,
  sectionSchema: pt.object,
  sectionGroups: pt.array,
  custom_css: pt.string,
  assetLoader: pt.object,
  theme: pt.object,
  overlayRootRef: pt.object,
};

function getSectionComponent(
  props,
  sectionSchema,
  theme,
  assetLoader,
  overlayRootRef,
) {
  const { sectionGroups } = props;

  function SectionWrapper(sectionProps) {
    return (
      <Section
        sectionSchema={sectionSchema}
        sectionGroups={sectionGroups}
        assetLoader={assetLoader}
        theme={theme}
        overlayRootRef={overlayRootRef}
        {...sectionProps}
      />
    );
  }

  return React.memo(SectionWrapper);
}

export default getSectionComponent;
